import {
  ChangeDetectionStrategy, ElementRef, HostListener, ViewChild, AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList,
} from '@angular/core'
import {
  merge, Subject,
} from 'rxjs'
import {
  debounceTime, distinctUntilChanged, filter, startWith, switchMap,
} from 'rxjs/operators'
import { SelectOptionComponent } from './select-option/select-option.component'
import { CommonModule } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  BaseControlComponent, ControlContainerComponent,
} from '@knorr-bremse-portals/ngx-components/internal/controls'
import {
  DOWN_ARROW, UP_ARROW, LEFT_ARROW, RIGHT_ARROW, ENTER, SPACE, hasModifierKey, TAB, ESCAPE, BACKSPACE,
} from '@knorr-bremse-portals/ngx-components'
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon'
import { LabelComponent } from '@knorr-bremse-portals/ngx-components/components/label'
import {
  iconArrowDown, iconSearch,
} from '@knorr-bremse-portals/icons'

const SEARCH_RESET_DELAY_MILLISECONDS = 800

@Component({
  selector: 'kb-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ControlContainerComponent, LabelComponent, IconComponent],
})
export class SelectComponent<T> extends BaseControlComponent<T | null> implements AfterContentInit {
  @Input()
  public floating?: ''

  @Input()
  public on?: 'background'

  @Input()
  public standalone = false

  @Input()
  public showRequired = true

  @Input('aria-label')
  public ariaLabel?: string

  @Input('aria-labelledby')
  public ariaLabelledby?: string

  @Input()
  public get value(): T | null { return this._value }

  public set value(newValue: T | null) {
    if (this.options) { // Reactive form triggers writeValue before public ngAfterContentInit, therefore _options is not populated
      this.options.forEach(option => option.deselect())
      this.options.find(option => option.value == newValue)?.select() // == is required, as newValue can be null & option.value can be undefined

      // _value is set through select change handler
      this.ref.markForCheck() // Needed for programmatic value updates e.g. ngModel
    } else {
      this._value = newValue
    }
  }

  private _value: T | null = null
  @Output() public readonly valueChange = new EventEmitter<T | null>()

  @Input()
  public valueComparator: (value1: T | null, value2: T | null) => boolean = (value1, value2) => value1 == value2

  protected get open(): boolean { return this._open }
  protected set open(value: boolean) {
    if (!this.disabled) {
      this._open = value
      this.ref.markForCheck()
      if (this._open) {
        this.scrollToOption(this.selectedOption)
      }
    }
  }

  private _open = false

  protected get ariaActiveDescendant(): string | null {
    return this._open ? this.selectedOption?.id : null
  }

  // @HostBinding('attr.aria-invalid') // TODO
  /* @HostBinding('attr.aria-describedby')
  get ariaDescribedby() {

  } */

  protected comboboxId = `kb-select-combobox-${this.uniqueId}`
  protected listboxId = `kb-select-listbox-${this.uniqueId}`
  protected iconArrowDown = iconArrowDown
  protected iconSearch = iconSearch

  protected selectedOption!: SelectOptionComponent<T>

  @ContentChildren(SelectOptionComponent, { descendants: true }) private options!: QueryList<SelectOptionComponent<T>>
  @ViewChild('optionsPanel', { read: ElementRef }) private optionsPanel!: ElementRef<HTMLDivElement>
  @ViewChild('comboboxElement', { read: ElementRef }) private comboboxElement!: ElementRef<HTMLDivElement>

  /** `View -> model callback called when value changes` */
  private onChange: (value: T | null) => void = () => undefined

  /** `View -> model callback called when select has been touched` */
  private onTouched: () => void = () => undefined

  public ngAfterContentInit(): void {
    this.setupSelectChangeHandler()

    this.selectInitialOption()

    this.setupBlurHandler()
  }

  protected onOptionsPanelClick() {
    this.open = false
  }

  private selectInitialOption(): void {
    let selectedOption
    const value = this._value
    if (value !== null) {
      selectedOption = this.options.find(option => option.value !== undefined && this.valueComparator(option.value, value))
    } else {
      if (!selectedOption) { // Get the first selected option
        selectedOption = this.options.find(option => option.selected)
      }
      if (!selectedOption) { // Get the first option without a value
        selectedOption = this.options.find(option => option.value === undefined)
      }
    }

    selectedOption?.select()
  }

  private setupSelectChangeHandler() {
    this.options.changes.pipe(
      startWith(this.options),
      switchMap(() => merge(...this.options.map(option => option.selectedChange))),
      filter(option => option.selected === true),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((selectedOption) => {
      // Deselect non selected options
      this.options
        .filter(option => option !== selectedOption)
        .forEach(option => option.deselect())

      this.selectedOption = selectedOption

      const newValue = selectedOption.value ?? null
      if (!this.valueComparator(this._value, newValue)) {
        this._value = newValue
        this.valueChange.emit(this.value)
        if (this.skipChanges === false) {
          this.onChange(this.value)
        }
      }

      this.ref.markForCheck() // This is needed if the currently selected option was removed and a new one was selected by _selectInitialOption
    })

    this.options.changes.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.selectInitialOption()
    })
  }

  private get selectedOptionIndex(): number {
    return this.getSelectableOptions().indexOf(this.selectedOption)
  }

  private getSelectableOptions(): SelectOptionComponent<T>[] {
    return this.options.filter(option => !option.disabled)
  }

  private navigateOption(next: boolean): void {
    const selectableOptions = this.getSelectableOptions()
    const targetOption = selectableOptions[((this.selectedOptionIndex + (next ? 1 : -1)) + selectableOptions.length) % selectableOptions.length]
    this.selectOption(targetOption)
  }

  private selectNextOption(): void {
    this.navigateOption(true)
  }

  private selectPreviousOption(): void {
    this.navigateOption(false)
  }

  private selectOption(option?: SelectOptionComponent<T>): void {
    if (option === undefined) {
      return
    }
    this.value = option.value ?? null
    this.scrollToOption(option)
  }

  private scrollToOption(option: SelectOptionComponent<T>): void {
    if (!this._open) {
      return
    }

    let height = 0
    for (const currentOption of this.options) {
      if (currentOption === option) {
        break
      }
      height += currentOption.getOffsetHeight()
    }

    this.optionsPanel.nativeElement.scrollTop = height
  }

  /** Handles all keydown events on the select. */
  @HostListener('keydown', ['$event'])
  protected handleKeydown(event: KeyboardEvent): void {
    if (!this.disabled) {
      if (this.open) {
        this.handleOpenKeydown(event)
      } else {
        this.handleClosedKeydown(event)
      }
    }
  }

  private handleArrowSelect(key: string): void {
    if (key === DOWN_ARROW) {
      this.selectNextOption()
    } else {
      this.selectPreviousOption()
    }
  }

  /** Handles keyboard events while the select is closed. */
  private handleClosedKeydown(event: KeyboardEvent): void {
    const key = event.key
    const isArrowKey = key === DOWN_ARROW || key === UP_ARROW
      || key === LEFT_ARROW || key === RIGHT_ARROW
    const isOpenKey = key === ENTER || key === SPACE

    // Open the select on ALT + arrow key to match the native <select>
    if ((isOpenKey && !hasModifierKey(event)) || (event.altKey && isArrowKey)) {
      event.preventDefault() // prevents the page from scrolling
      this.open = true
      return
    }

    const isVerticalArrowKey = key === DOWN_ARROW || key === UP_ARROW
    // Open the select and select the next / previous element
    if (isVerticalArrowKey) {
      event.preventDefault() // prevents the page from scrolling
      this.open = true
      this.handleArrowSelect(key)
      return
    }
  }

  /** Handles keyboard events when the selected is open. */
  private handleOpenKeydown(event: KeyboardEvent): void {
    const key = event.key
    const isVerticalArrowKey = key === DOWN_ARROW || key === UP_ARROW

    if ((isVerticalArrowKey && event.altKey) || key === TAB || key === ESCAPE) {
      // Close the select on ALT + arrow key or Tab to match the native <select>
      event.preventDefault()
      this.open = false
    } else if (isVerticalArrowKey) {
      this.handleArrowSelect(key)
      event.preventDefault() // Prevent page scrolling
    } else if (key === ENTER && !hasModifierKey(event)) {
      this.open = false // select already active
    } else if (key === BACKSPACE || key.length === 1) {
      this.handleKeyboardSearch(key)
    }
  }

  private searchOptions(query: string): SelectOptionComponent<T> | undefined {
    const selectableOptions = this.getSelectableOptions()

    const searchStringArray = query.split('')
    const allSameCharacter = searchStringArray.every(character => character === searchStringArray[0])
    if (allSameCharacter) {
      const foundOptions = selectableOptions.filter((option) => {
        return option.getLabelText().toLowerCase().startsWith(query[0])
      })
      if (foundOptions.length === 0) {
        return undefined
      }

      const selectedOptionIndex = foundOptions.indexOf(this.selectedOption)
      return foundOptions[(selectedOptionIndex + 1) % foundOptions.length]
    }

    return selectableOptions.find((option) => {
      return option.getLabelText().toLowerCase().startsWith(query)
    })
  }

  private searchString = ''
  private searchStringTimeout?: number

  private handleKeyboardSearch(key: string) {
    if (this.searchStringTimeout !== undefined) {
      window.clearTimeout(this.searchStringTimeout)
    }

    this.searchStringTimeout = window.setTimeout(() => {
      this.searchString = ''
    }, SEARCH_RESET_DELAY_MILLISECONDS)

    if (key === BACKSPACE) {
      this.searchString = this.searchString.slice(0, -1)
    } else {
      this.searchString += key.toLowerCase()
    }

    const foundOption = this.searchOptions(this.searchString)
    if (foundOption !== undefined) {
      this.selectOption(foundOption)
    } else {
      this.searchString = ''
    }
  }

  private focusChanged = new Subject<boolean>()

  protected onFocus(): void {
    this.focusChanged.next(true)
  }

  protected onBlur(event: FocusEvent): void {
    if (event.relatedTarget === this.optionsPanel.nativeElement) {
      this.comboboxElement.nativeElement.focus()
      return
    }
    this.focusChanged.next(false)
  }

  private setupBlurHandler() {
    this.focusChanged.pipe(
      debounceTime(1),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((focused) => {
      if (!this.disabled && !focused) {
        this.open = false
        this.onTouched()
      }
    })
  }

  private skipChanges = false
  public writeValue(value: T): void {
    this.skipChanges = true
    this.value = value
    this.skipChanges = false
  }

  public registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }
}
